<template>
  <div class="work-permissions-editor" :class="{ 'is-embedded': !showHeader }">
    <header v-if="showHeader" class="editor-header">
      <div>
        <h2>Work permissions report</h2>
        <div class="report-subtitle">
          Live supplier data
          · Auto-refreshes every 20s
          <template v-if="generatedAtDisplay !== '—'">
            · Updated {{ generatedAtDisplay }}
          </template>
        </div>
      </div>
    </header>

    <div v-if="isLoading && !report" class="card placeholder">Loading live work permissions report…</div>
    <div v-else-if="errorMessage && !report" class="card error">{{ errorMessage }}</div>
    <div v-else-if="!report" class="card placeholder">
      No live work permissions data available.
    </div>
    <template v-else>
      <div v-if="errorMessage" class="card error" role="status">{{ errorMessage }}</div>
      <!-- No overflow here, so the column headers stick to the top while the page scrolls. -->
      <div
        class="table-wrapper"
        role="region"
        aria-label="Work permissions by supplier"
      >
        <table class="snapshot-table">
          <colgroup>
            <col class="col-supplier" />
            <col class="col-list" />
            <col class="col-date-from" />
            <col class="col-expire" />
            <col class="col-status" />
            <col class="col-check-date" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Supplier</th>
              <th scope="col">List</th>
              <th scope="col">Date From</th>
              <th scope="col">Right to work until</th>
              <th scope="col">Status</th>
              <th scope="col">Check Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!reportRows.length">
              <td colspan="6" class="empty-cell">No supplier data available.</td>
            </tr>
            <tr
              v-for="row in reportRows"
              :key="supplierRowKey(row)"
              class="table-row"
              :class="{ active: activeSubmenuRowKey === supplierRowKey(row) }"
              @click="toggleSubmenu(row, $event)"
              @contextmenu.prevent="openSubmenu(row, $event)"
            >
              <td class="supplier-cell">
                <span class="supplier-name" :title="row.supplierName || undefined">
                  {{ row.supplierName || '—' }}
                </span>
              </td>
              <td>
                <span v-if="formatListType(row) !== '—'" class="list-chip">
                  {{ formatListType(row) }}
                </span>
                <span v-else class="empty-value">—</span>
              </td>
              <td class="date-cell is-secondary">{{ formatDate(isUnlimited(row) || getRightToWorkFollowUpBasis(row) !== 'permission_expiry' ? null : row.rightToWorkDocumentDate) }}</td>
              <td class="date-cell">{{ formatDate(deriveRightToWorkCheckDeadline(row)) }}</td>
              <td class="status-cell">
                <span class="status-pill" :class="statusPillClass(row.rightToWorkStatus)">
                  {{ formatStatus(row.rightToWorkStatus) }}
                </span>
              </td>
              <td class="date-cell is-secondary">{{ formatDate(row.rightToWorkCheckDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="activeSubmenuRow"
        class="submenu"
        :style="submenuStyle"
        v-click-outside="closeSubmenu"
      >
        <button class="submenu-btn" type="button" @click.stop="showActiveSupplierInCatalog">
          Show in catalog
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from 'vue';
import { useCatalogStore } from '@/stores/catalog';
import { useTabsStore } from '@/stores/tabs';
import { useWorkPermissionsReportsStore } from '@/stores/workPermissionsReports';
import {
  formatRightToWorkListLabel,
  formatRightToWorkStatusLabel,
  deriveRightToWorkCheckDeadline,
  getRightToWorkFollowUpBasis,
} from '@/utils/rightToWork';
import CatalogView from '@/views/catalog/CatalogView.vue';

defineProps({
  reportId: {
    type: [String, Number],
    default: null,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
});

const store = useWorkPermissionsReportsStore();
const catalogStore = useCatalogStore();
const tabsStore = useTabsStore();
const { proxy } = getCurrentInstance();
const logError = proxy?.$logError;
const toast = proxy?.$toast;

const errorMessage = computed(() => store.error || '');
const activeSubmenuRowKey = ref(null);
const submenuPosition = reactive({ top: 0, left: 0 });
const CATALOG_MENU_TAB_KEY = 'catalogs';

const report = computed(() => store.liveReport);
const isLoading = computed(() => store.isLoading);
const reportRows = computed(() => {
  const rows = report.value?.eligibilitySnapshot;
  return Array.isArray(rows) ? rows : [];
});
const supplierRowKey = (row = {}) =>
  String(row?.supplierDocumentId || row?.supplierId || '');
const activeSubmenuRow = computed(
  () =>
    reportRows.value.find((row) => supplierRowKey(row) === activeSubmenuRowKey.value)
    || null,
);
const submenuStyle = computed(() => ({
  top: `${submenuPosition.top}px`,
  left: `${submenuPosition.left}px`,
}));

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const UTC_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const formatDate = (value) => {
  if (!value) return '—';

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (DATE_ONLY_PATTERN.test(trimmedValue)) {
      const dateOnly = new Date(`${trimmedValue}T00:00:00.000Z`);
      if (!Number.isNaN(dateOnly.getTime())) {
        return UTC_DATE_FORMATTER.format(dateOnly);
      }
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return UTC_DATE_FORMATTER.format(date);
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatListType = (entry = {}) => {
  const label = formatRightToWorkListLabel(entry?.rightToWorkListType);
  if (label !== '—') return label;
  return entry?.rightToWorkExpiryDate ? 'Legacy' : '—';
};

const isUnlimited = (row) => String(row?.rightToWorkListType || '').trim().toLowerCase() === 'list_a';

const formatStatus = (value) => formatRightToWorkStatusLabel(value);
const generatedAtDisplay = computed(() => formatDateTime(report.value?.metadata?.generatedAt));

const statusPillClass = (value) => ({
  'is-valid': value === 'valid' || value === 'completed',
  'is-warning': value === 'needs_verification' || value === 'not_started' || value === 'expiring_soon' || value === 'due_soon',
  'is-danger': value === 'expired' || value === 'overdue',
  'is-muted': value === 'missing' || value === 'ok' || value === 'not_required',
});

const canOpenSupplierRow = (row) =>
  row?.supplierId !== null && row?.supplierId !== undefined && row?.supplierId !== '';

const calculateSubmenuPosition = (event) => {
  const submenuWidth = 180;
  const submenuHeight = 52;
  const padding = 12;

  let left = event?.clientX || 0;
  let top = event?.clientY || 0;

  if (typeof window !== 'undefined') {
    if (left + submenuWidth > window.innerWidth - padding) {
      left = window.innerWidth - submenuWidth - padding;
    }
    if (top + submenuHeight > window.innerHeight - padding) {
      top = Math.max(padding, window.innerHeight - submenuHeight - padding);
    }
  }

  submenuPosition.left = Math.max(padding, left);
  submenuPosition.top = Math.max(padding, top);
};

const closeSubmenu = () => {
  activeSubmenuRowKey.value = null;
};

const toggleSubmenu = (row, event) => {
  event?.stopPropagation();
  if (!canOpenSupplierRow(row)) return;

  const rowKey = supplierRowKey(row);
  if (activeSubmenuRowKey.value === rowKey) {
    closeSubmenu();
    return;
  }

  activeSubmenuRowKey.value = rowKey;
  nextTick(() => {
    calculateSubmenuPosition(event);
  });
};

const openSubmenu = (row, event) => {
  event?.preventDefault();
  event?.stopPropagation();
  if (!canOpenSupplierRow(row)) return;

  activeSubmenuRowKey.value = supplierRowKey(row);
  nextTick(() => {
    calculateSubmenuPosition(event);
  });
};

const openSupplierCatalogById = async (supplierId) => {
  if (supplierId === null || supplierId === undefined || supplierId === '') return;

  try {
    await catalogStore.bootstrap?.();
    const supplierCatalog = catalogStore.catalogByName?.('Suppliers');
    if (!supplierCatalog?.id) {
      toast?.error?.('Suppliers catalog not available');
      return;
    }

    catalogStore.setActiveCatalog(supplierCatalog.id);
    catalogStore.setActiveCatalogItemId(supplierCatalog.id, supplierId);

    const existing = tabsStore.findMatchingTab({
      component: CatalogView,
      itemId: supplierCatalog.id,
      section: 'menu',
      tabKey: CATALOG_MENU_TAB_KEY,
    });
    if (existing) {
      tabsStore.updateTab(existing.id, {
        props: { catalogId: supplierCatalog.id },
        title: supplierCatalog.name || 'Suppliers',
        itemId: supplierCatalog.id,
        section: 'menu',
        tabKey: CATALOG_MENU_TAB_KEY,
      });
      tabsStore.activateTab(existing.id);
      return;
    }

    const tab = tabsStore.createTab({
      component: CatalogView,
      title: supplierCatalog.name || 'Suppliers',
      props: { catalogId: supplierCatalog.id },
      itemId: supplierCatalog.id,
      section: 'menu',
      tabKey: CATALOG_MENU_TAB_KEY,
    });
    if (tab) {
      tabsStore.activateTab(tab.id);
    }
  } catch (error) {
    toast?.error?.('Unable to open supplier catalog');
    logError?.(error, 'WorkPermissionsEditorView::openSupplierCatalogById');
  }
};

const showActiveSupplierInCatalog = async () => {
  const supplierId = activeSubmenuRow.value?.supplierId;
  closeSubmenu();
  await openSupplierCatalogById(supplierId);
};

const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  },
};

const loadReport = async () => {
  try {
    await store.fetchLiveReport();
  } catch (err) {
    logError?.(err, 'WorkPermissionsEditorView loadReport');
  }
};

onMounted(() => {
  store.startLivePolling({ immediate: false });
  void loadReport();
});

onBeforeUnmount(() => {
  store.stopLivePolling();
});
</script>

<style scoped>
/* Uses the list styles of the Payments and journal pages. */
.work-permissions-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  color: var(--text-color);
  font: var(--ap-type-body);
}

.work-permissions-editor.is-embedded {
  min-height: 0;
  padding: 0;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.editor-header h2 {
  margin: 0;
  font: var(--ap-type-title);
}

.report-subtitle {
  margin-top: 2px;
  color: var(--text-color-secondary);
  font: var(--ap-type-caption);
}

.card {
  margin: 0;
  padding: 12px 16px;
  color: var(--text-color-secondary);
  font: var(--ap-type-control);
}

.card.error {
  color: var(--noc-red, #c45151);
}

/* The page scrolls the table (narrow screens scroll it sideways too), so the headers can stick. */
.table-wrapper {
  min-height: 0;
}

.snapshot-table {
  width: 100%;
  min-width: 920px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  border-inline: 1px solid var(--border-color);
  background: var(--noc-background, #fff);
  color: var(--text-color);
  font: var(--ap-type-body);
}

.snapshot-table .col-supplier {
  width: 24%;
}

.snapshot-table .col-list {
  width: 10%;
}

.snapshot-table .col-status {
  width: 15%;
}

.snapshot-table .col-date-from,
.snapshot-table .col-check-date,
.snapshot-table .col-expire {
  width: 17%;
}

.snapshot-table th,
.snapshot-table td {
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-color);
  text-align: left;
  vertical-align: middle;
}

.snapshot-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  height: 38px;
  box-sizing: border-box;
  border-top: 1px solid var(--border-color);
  background: #fafafa;
  color: var(--text-color);
  font: var(--ap-type-table-label);
  font-weight: 600;
  white-space: nowrap;
}

.snapshot-table tbody tr:not(:last-child) td:first-child {
  border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.table-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

/* 38px rows including the bottom border; the 22px pills stay centred. */
.snapshot-table .table-row td {
  height: 38px;
  box-sizing: border-box;
  padding-block: 0;
}

.table-row.active {
  background: var(--noc-blue-05);
}

@media (hover: hover) {
  .table-row:hover {
    background: var(--noc-blue-05);
  }
}

.supplier-cell {
  font: var(--ap-type-control);
}

.supplier-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.list-chip {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 8px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--noc-background-secondary, #fafafb);
  color: var(--text-color-secondary);
  font: var(--ap-type-label);
  white-space: nowrap;
}

.date-cell {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Date From and Check Date are for reference; Right to work until drives the status. */
.date-cell.is-secondary {
  color: var(--text-color-secondary);
}

.empty-value {
  color: var(--text-color-secondary);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: 22px;
  padding: 2px 8px;
  overflow: hidden;
  border-radius: 4px;
  background: var(--noc-background-secondary, #f3f4f6);
  color: var(--text-color);
  font: var(--ap-type-label);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-pill.is-valid {
  background: var(--report-status-success-background, rgba(39, 163, 118, 0.12));
  color: var(--report-status-success-color, #1f7f5d);
}

.status-pill.is-warning {
  background: var(--report-status-warning-background, rgba(205, 148, 48, 0.14));
  color: var(--report-status-warning-color, #9a6610);
}

.status-pill.is-danger {
  background: var(--report-status-danger-background, rgba(196, 81, 81, 0.12));
  color: var(--report-status-danger-color, #b33d3d);
}

.status-pill.is-muted {
  background: var(--report-status-muted-background, rgba(107, 114, 128, 0.12));
  color: var(--report-status-muted-color, #4b5563);
}

.empty-cell {
  padding: 24px 16px;
  color: var(--text-color-secondary);
  text-align: center;
}

/* The row menu opens like the journal row menus. */
.submenu {
  position: fixed;
  z-index: 1000;
  min-width: 160px;
  padding: 4px 0;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--noc-background);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  animation: row-menu-enter 160ms ease-out both;
}

.submenu-btn {
  display: block;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  color: var(--text-color);
  font: var(--ap-type-control);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.submenu-btn:hover {
  background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

@keyframes row-menu-enter {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .submenu {
    animation: none;
  }
}

@media (max-width: 1100px) {
  .editor-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
